import {useCallback, useEffect, useRef, useState} from 'react';
import {useInterval} from '~/common/hooks/useInterval';
import {ACTIONS} from './constants';

function getWorkerURL(url) {
  const content = `importScripts( "${url}" );`;
  return window.URL.createObjectURL(new Blob([content], {type: 'text/javascript'}));
}

let worker = null;

evite.when('add-guests-webworker').then((url) => {
  const workerURL = getWorkerURL(new URL(url, window.location.origin));

  worker = new SharedWorker(workerURL, {
    name: 'Contacts Worker',
  });
});

const THREE_MINUTES_IN_MS = 180000;
const MAX_LIST_LENGTH = 500;
const ENABLE_DEBUG = window.location.hostname !== 'www.evite.com';

const log = (...items) => {
  if (ENABLE_DEBUG) {
    if (typeof items[0] === 'string' && items.length === 1) {
      // eslint-disable-next-line no-console
      console.log(`%c${items[0]}`, 'color: chartreuse');
    } else {
      // eslint-disable-next-line no-console
      console.log(...items);
    }
  }
};

const sendMessage = (action, data) => {
  worker?.port?.postMessage({
    cdbaction: action,
    extras: data,
  });
};

/**
 * Main Hook
 */
export const useContactsWorker = (eventId) => {
  const isWorkerReady = useRef(false);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const refreshContacts = useCallback(async () => {
    sendMessage(ACTIONS.POPULATE, {eventId});
  }, [eventId]);

  useInterval(refreshContacts, THREE_MINUTES_IN_MS);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.results) {
        setContacts(event.data.results);
      } else if (event.data.results === null) {
        evite.error('Failed to query contacts', event.data.error);
      } else if (event.data.success) {
        log('Message handler success');
        isWorkerReady.current = true;
        setSearchTerm('');
      } else if (event.data.success === false) {
        log(`Message handler failure: ${event.data.error}`);
        isWorkerReady.current = false;
      }
    };
    const failureHandler = (err) => {
      evite.error('useContactsWorker worker error', err);
    };
    worker?.port?.addEventListener('error', failureHandler);
    worker?.port?.addEventListener('message', handleMessage);
    worker?.port?.addEventListener('messageerror', failureHandler);
    worker?.port?.start();
    sendMessage(ACTIONS.POPULATE, {eventId});
    return () => {
      worker?.port?.removeEventListener('message', handleMessage);
      worker?.port?.removeEventListener('error', handleMessage);
      worker?.port?.removeEventListener('messageerror', handleMessage);
    };
  }, [eventId]);

  useEffect(() => {
    if (searchTerm) {
      sendMessage('query', {eventId, searchTerm, limit: MAX_LIST_LENGTH});
    }
  }, [eventId, searchTerm]);

  return {
    isWorkerReady: isWorkerReady.current,
    contacts,
    setSearchTerm,
    refreshContacts,
  };
};

/**
 * These functions are called outside the scope of the hook
 */
export const repopulateContacts = (eventId) => {
  log(`repopulateContacts: ${eventId}`);
  window.setTimeout(() => {
    log(`repopulateContacts: sending populate message`);
    sendMessage(ACTIONS.POPULATE, {eventId});
  }, 200);
};
